/**
 * フォーム一覧。マーケティング/営業の2タブで分離 (Phase 1+)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §1
 */
import {
  CalendarClock,
  ChevronDown,
  ClipboardList,
  Copy,
  Edit,
  ExternalLink,
  FileText,
  Inbox,
  MessageSquare,
  Package as PackageIcon,
  Plus,
  Sparkles,
  Trash2,
  UserCog,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateForm,
  useDeleteForm,
  useForms,
  type FormRecord,
} from '@/hooks/useForms';
import { formatDate } from '@/lib/firestoreTime';

import { FormScheduleDialog } from './FormScheduleDialog';
import {
  SALES_FORM_TEMPLATE_LIST,
  SALES_FORM_TEMPLATES,
  type SalesFormTemplateKey,
} from './salesFormTemplates';

import type { FormStatus, FormTag } from '@goenhub/shared';

const STATUS_LABEL: Record<FormStatus, string> = {
  draft: '下書き',
  published: '公開中',
  closed: '受付終了',
};

const STATUS_BADGE: Record<FormStatus, 'draft' | 'approved' | 'default'> = {
  draft: 'draft',
  published: 'approved',
  closed: 'default',
};

const TEMPLATE_ICON: Record<SalesFormTemplateKey, typeof FileText> = {
  document_request: FileText,
  briefing: MessageSquare,
  quote: PackageIcon,
  customer_info: UserCog,
};

/** tag 未設定の form は marketing 扱い (後方互換) */
function getEffectiveTag(f: FormRecord): FormTag {
  return f.tag ?? 'marketing';
}

export function FormsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useForms();
  const createMutation = useCreateForm();
  const deleteMutation = useDeleteForm();
  const [creating, setCreating] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<FormRecord | null>(null);
  const [activeTab, setActiveTab] = useState<FormTag>('marketing');
  const [salesMenuOpen, setSalesMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    if (!salesMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setSalesMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [salesMenuOpen]);

  const filtered = (data ?? []).filter((f) => getEffectiveTag(f) === activeTab);

  const handleCreateMarketing = async () => {
    setCreating(true);
    try {
      const id = await createMutation.mutateAsync({
        name: '無題のフォーム',
        title: '無題のフォーム',
        status: 'draft',
        elements: [],
        tag: 'marketing',
        createdBy: user?.uid ?? '',
      });
      navigate(`/forms/${id}`);
    } catch (err) {
      toast.error(`作成失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateSales = async (key: SalesFormTemplateKey) => {
    setSalesMenuOpen(false);
    setCreating(true);
    try {
      const tpl = SALES_FORM_TEMPLATES[key];
      const id = await createMutation.mutateAsync({
        name: tpl.formName,
        title: tpl.formTitle,
        description: tpl.formDescription,
        status: 'draft',
        elements: tpl.elements,
        tag: 'sales',
        dealStageHint: tpl.dealStageHint,
        category: tpl.category,
        // 顧客情報確認・更新テンプレは「最新の申告で上書き」を既定にする
        ...(tpl.contactOverwritePolicy
          ? { contactOverwritePolicy: tpl.contactOverwritePolicy }
          : {}),
        createdBy: user?.uid ?? '',
      });
      navigate(`/forms/${id}`);
    } catch (err) {
      toast.error(`作成失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (f: FormRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreating(true);
    try {
      const id = await createMutation.mutateAsync({
        name: `${f.name} (コピー)`,
        title: f.title,
        description: f.description,
        elements: f.elements,
        thanksMessage: f.thanksMessage,
        redirectUrl: f.redirectUrl,
        theme: f.theme,
        status: 'draft',
        publicStartAt: null,
        publicEndAt: null,
        campaignIds: f.campaignIds,
        contactTags: f.contactTags,
        tag: f.tag,
        dealStageHint: f.dealStageHint,
        category: f.category,
        contactOverwritePolicy: f.contactOverwritePolicy,
        // 完了通知の文面も引き継ぐ（enabled は useCreateForm 側で必ず true になる）
        completionNotification: f.completionNotification ?? null,
        createdBy: user?.uid ?? '',
      });
      toast.success('複製しました');
      navigate(`/forms/${id}`);
    } catch (err) {
      toast.error(`複製失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (f: FormRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `フォーム「${f.name}」を削除します。回収済みデータ (form_submissions) は履歴として残ります。`,
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync(f.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleOpenPublic = (f: FormRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (f.status !== 'published') {
      toast.error('「公開中」のフォームのみ公開ページにアクセスできます');
      return;
    }
    window.open(`/public/forms/${f.id}`, '_blank', 'noopener');
  };

  const handleOpenSubmissions = (f: FormRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/forms/${f.id}/submissions`);
  };

  const handleOpenSchedule = (f: FormRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setScheduleForm(f);
  };

  const renderActions = () => {
    if (!canEdit) return null;
    if (activeTab === 'marketing') {
      return (
        <Button
          type="button"
          variant="primary"
          onClick={handleCreateMarketing}
          disabled={creating}
        >
          {creating ? <Spinner className="size-3 mr-1" /> : <Plus />}
          新規フォーム
        </Button>
      );
    }
    return (
      <div ref={menuRef} className="relative">
        <Button
          type="button"
          variant="primary"
          onClick={() => setSalesMenuOpen(!salesMenuOpen)}
          disabled={creating}
        >
          {creating ? <Spinner className="size-3 mr-1" /> : <Sparkles />}
          営業フォーム作成
          <ChevronDown className="size-3.5 ml-1" />
        </Button>
        {salesMenuOpen && (
          <div className="absolute right-0 mt-1 z-20 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg overflow-hidden min-w-[280px]">
            {SALES_FORM_TEMPLATE_LIST.map((tpl) => {
              const Icon = TEMPLATE_ICON[tpl.key];
              return (
                <button
                  key={tpl.key}
                  type="button"
                  onClick={() => handleCreateSales(tpl.key)}
                  className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-[var(--bg-hover)] border-b border-[var(--border)] last:border-b-0"
                >
                  <Icon className="size-4 mt-0.5 text-[var(--accent)]" />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium">{tpl.label}</div>
                    <div className="text-[11px] text-[var(--text-muted)] leading-relaxed">
                      {tpl.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderList = (forms: FormRecord[]) => {
    if (forms.length === 0) {
      return (
        <EmptyState
          icon={ClipboardList}
          title={
            activeTab === 'sales'
              ? '営業フォームがまだありません'
              : 'マーケティングフォームがまだありません'
          }
          description={
            activeTab === 'sales'
              ? '「営業フォーム作成」から雛形 (資料請求 / 個別説明申込 / 見積依頼 / 顧客情報確認・更新) を選んで作成できます。'
              : '「新規フォーム」から作成できます。フィールド定義 → 公開設定 → URL 共有の流れで使用します。'
          }
        />
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {forms.map((f) => {
          const isSales = getEffectiveTag(f) === 'sales';
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => navigate(`/forms/${f.id}`)}
              className="text-left rounded bg-[var(--bg-surface)] border border-[var(--border)] hover:border-[var(--accent)] transition-colors p-4 group"
            >
              <div className="flex items-start gap-2 mb-2">
                {isSales ? (
                  <Sparkles className="size-5 text-[var(--accent)] flex-shrink-0 mt-0.5" />
                ) : (
                  <ClipboardList className="size-5 text-[var(--accent-purple)] flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-sm truncate">{f.name}</h3>
                    <Badge variant={STATUS_BADGE[f.status]}>
                      {STATUS_LABEL[f.status]}
                    </Badge>
                    {isSales && f.dealStageHint && (
                      <Badge variant="accent">
                        {f.dealStageHint === 'document_requested'
                          ? '資料請求'
                          : f.dealStageHint === 'briefing'
                            ? '個別説明'
                            : '見積'}
                      </Badge>
                    )}
                    {isSales && f.category === 'customer_info' && (
                      <Badge
                        variant="review"
                        title="顧客情報確認・更新フォーム。送信されても商談 (Deal) は作成されません。"
                      >
                        顧客情報
                      </Badge>
                    )}
                  </div>
                  {f.description && (
                    <p className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                      {f.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 mb-3 flex-wrap">
                <span>
                  {f.elements?.filter((e) => e.kind === 'field').length ?? 0} 質問 ・{' '}
                  {f.elements?.length ?? 0} 要素
                </span>
                <span>回収 {f.submissionCount ?? 0}</span>
                <span>更新 {formatDate(f.updatedAt)}</span>
              </div>
              {canEdit && (
                <div className="flex justify-end gap-1 pt-3 border-t border-[var(--border)]">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleOpenSubmissions(f, e)}
                    title="回収一覧を開く"
                  >
                    <Inbox className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleOpenSchedule(f, e)}
                    title="回答者へメール予約配信"
                  >
                    <CalendarClock className="size-3.5" />
                  </Button>
                  {f.status === 'published' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleOpenPublic(f, e)}
                      title="公開ページを開く"
                    >
                      <ExternalLink className="size-3.5" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/forms/${f.id}`);
                    }}
                    title="編集"
                  >
                    <Edit className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => void handleDuplicate(f, e)}
                    disabled={creating}
                    title="複製"
                  >
                    <Copy className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => void handleDelete(f, e)}
                    title="削除"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Screen
      title="フォーム"
      description="マーケティング (LP問合せ等) と営業 (資料請求 / 個別説明 / 見積 / 顧客情報確認・更新) でタブ分離。送信時の挙動が異なります。"
      actions={renderActions()}
    >
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as FormTag)}
        >
          <TabsList>
            <TabsTrigger value="marketing">マーケティング</TabsTrigger>
            <TabsTrigger value="sales">営業</TabsTrigger>
          </TabsList>
          <TabsContent value="marketing">{renderList(filtered)}</TabsContent>
          <TabsContent value="sales">{renderList(filtered)}</TabsContent>
        </Tabs>
      )}

      {scheduleForm && (
        <FormScheduleDialog
          formId={scheduleForm.id}
          formName={scheduleForm.name}
          open={true}
          onOpenChange={(o) => {
            if (!o) setScheduleForm(null);
          }}
        />
      )}
    </Screen>
  );
}
